import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useSWRConfig } from "swr";
import { useTranslation } from "react-i18next";

import { ApiError, apiJson } from "../../api/client";
import { useGradeDistribution } from "../../api/hooks";
import type { Language, Project, ScoreFormat } from "../../api/types";
import { toast } from "../../lib/toast";
import { UploadPapersDialog } from "../UploadPapersDialog";
import { EditProjectModal } from "./EditProjectModal";
import { WeightsPanel } from "./WeightsPanel";
import { DetailsCard } from "./primitives/DetailsCard";
import { MetaRow } from "./primitives/MetaRow";
import "../CreateProjectModal.css";

const PROJECTS_KEY = "/api/projects/?archived=0";
const ARCHIVED_PROJECTS_KEY = "/api/projects/?archived=1";

const LANGUAGE_KEY: Record<string, string> = { en: "common.english", de: "common.german" };
const SCORE_FORMAT_LABEL: Record<ScoreFormat, string> = {
  "0-100": "0–100",
  "0-15": "0–15",
  "A-F": "A–F",
  "1-6": "1–6",
};
// Click-to-cycle order for the meta chips (mirrors the project form's options).
const LANGUAGES: Language[] = ["en", "de"];
const SCORE_FORMATS: ScoreFormat[] = ["0-15", "1-6", "A-F", "0-100"];

export function ProjectPanel({ project }: { project: Project }) {
  const { t } = useTranslation();
  const { mutate } = useSWRConfig();
  const [, setParams] = useSearchParams();
  const [uploadOpen, setUploadOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [weightsOpen, setWeightsOpen] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState<null | "zip" | "archive">(null);
  const [exportError, setExportError] = useState<string | null>(null);
  const [exportNotice, setExportNotice] = useState<string | null>(null);
  const [renaming, setRenaming] = useState(false);
  const [nameInput, setNameInput] = useState("");
  const [savingName, setSavingName] = useState(false);
  const [savingMeta, setSavingMeta] = useState(false);
  const [processingAll, setProcessingAll] = useState(false);

  const lw = project.rubric_config?.language_weight ?? 0.4;
  const cw = project.rubric_config?.content_weight ?? 0.6;
  const splitSum = lw + cw || 1;
  const weightsSplit = `${Math.round((lw / splitSum) * 100)}/${Math.round((cw / splitSum) * 100)}`;

  const total = project.papers.length;
  const completed = project.papers.filter((p) => p.status === "completed").length;
  const pct = total === 0 ? 0 : Math.round((completed / total) * 100);
  const anyExported = project.papers.some((p) => p.exported_at != null);
  const hasPapers = total > 0;
  // Papers that need a pipeline run: not currently in flight and not fully done
  // (covers freshly uploaded papers and ones whose annotations were wiped, which
  // clears the stage flags without resetting status).
  const runnable = project.papers.filter(
    (p) =>
      !p.archived &&
      p.status !== "staged" &&
      p.status !== "processing" &&
      !(p.ocr_done && p.language_done && p.content_done),
  );
  const subtitle = new Date(project.created_at).toLocaleDateString(undefined, {
    month: "long",
    year: "numeric",
  });

  async function onExportProject() {
    setExporting("zip");
    setExportError(null);
    setExportNotice(null);
    try {
      const result = await apiJson<{
        ok: boolean;
        included: string[];
        stale: string[];
        missing_export: string[];
      }>(`/api/projects/${project.id}/export`, { method: "POST" });
      const parts: string[] = [];
      if (result.stale.length) parts.push(`Stale (re-export needed): ${result.stale.join(", ")}.`);
      if (result.missing_export.length) {
        parts.push(`Skipped (not yet exported): ${result.missing_export.join(", ")}.`);
      }
      if (parts.length) setExportNotice(parts.join(" "));
      triggerDownload(`/api/projects/${project.id}/export?ts=${Date.now()}`, `project-${project.id}.zip`);
    } catch (err) {
      setExportError(extractDetail(err) ?? (err as Error).message);
    } finally {
      setExporting(null);
    }
  }

  async function onExportArchive() {
    setExporting("archive");
    setExportError(null);
    setExportNotice(null);
    try {
      await apiJson(`/api/projects/${project.id}/export_archive`, { method: "POST" });
      triggerDownload(`/api/projects/${project.id}/export_archive?ts=${Date.now()}`);
    } catch (err) {
      setExportError(extractDetail(err) ?? (err as Error).message);
    } finally {
      setExporting(null);
    }
  }

  async function onArchiveProject() {
    setArchiving(true);
    try {
      await apiJson(`/api/projects/${project.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ archived: true }),
      });
      await mutate(PROJECTS_KEY);
      setParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete("project");
        next.delete("paper");
        return next;
      });
      toast("Project archived");
    } catch (err) {
      toast(extractDetail(err) ?? (err as Error).message);
    } finally {
      setArchiving(false);
    }
  }

  function onStartRename() {
    setNameInput(project.name);
    setRenaming(true);
  }

  function onCancelRename() {
    setRenaming(false);
    setNameInput("");
  }

  async function onSaveRename() {
    const trimmed = nameInput.trim();
    if (!trimmed || trimmed === project.name) { onCancelRename(); return; }
    setSavingName(true);
    try {
      await apiJson(`/api/projects/${project.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: trimmed }),
      });
      await Promise.all([mutate(PROJECTS_KEY), mutate(ARCHIVED_PROJECTS_KEY)]);
      setRenaming(false);
    } catch (err) {
      toast(extractDetail(err) ?? (err as Error).message);
    } finally {
      setSavingName(false);
    }
  }

  async function onDeleteProject() {
    setDeleting(true);
    try {
      await apiJson(`/api/projects/${project.id}`, { method: "DELETE" });
      await Promise.all([mutate(PROJECTS_KEY), mutate(ARCHIVED_PROJECTS_KEY)]);
      setParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete("project");
        next.delete("paper");
        return next;
      });
      toast("Project deleted");
    } catch (err) {
      toast(extractDetail(err) ?? (err as Error).message);
    } finally {
      setDeleting(false);
      setDeleteOpen(false);
    }
  }

  async function onUnarchiveProject() {
    setArchiving(true);
    try {
      await apiJson(`/api/projects/${project.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ archived: false }),
      });
      await Promise.all([mutate(PROJECTS_KEY), mutate(ARCHIVED_PROJECTS_KEY)]);
      setParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete("project");
        next.delete("paper");
        return next;
      });
      toast("Project restored");
    } catch (err) {
      toast(extractDetail(err) ?? (err as Error).message);
    } finally {
      setArchiving(false);
    }
  }

  async function patchProject(body: Record<string, unknown>) {
    setSavingMeta(true);
    try {
      await apiJson(`/api/projects/${project.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      await mutate(PROJECTS_KEY);
      // Score format change relabels the distribution buckets.
      if ("score_format" in body) await mutate(`/api/projects/${project.id}/grade_distribution`);
    } catch (err) {
      toast(extractDetail(err) ?? (err as Error).message);
    } finally {
      setSavingMeta(false);
    }
  }

  function cycleLanguage() {
    const next = LANGUAGES[(LANGUAGES.indexOf(project.language) + 1) % LANGUAGES.length];
    void patchProject({ language: next });
  }

  function cycleScoreFormat() {
    const next = SCORE_FORMATS[(SCORE_FORMATS.indexOf(project.score_format) + 1) % SCORE_FORMATS.length];
    void patchProject({ score_format: next });
  }

  async function onRunProject() {
    if (runnable.length === 0) return;
    setProcessingAll(true);
    try {
      await Promise.all(
        runnable.map((p) => {
          // Never-processed papers use /process; anything already run (incl.
          // wiped) uses /reprocess, mirroring the single-paper panel.
          const neverProcessed =
            p.status === "queued" && !p.ocr_done && !p.language_done && !p.content_done;
          const endpoint = neverProcessed
            ? `/api/papers/${p.id}/process`
            : `/api/papers/${p.id}/reprocess`;
          return apiJson(endpoint, { method: "POST" });
        }),
      );
      await mutate(PROJECTS_KEY);
      toast(t("project_panel.processing_started", { count: runnable.length }));
    } catch (err) {
      toast(extractDetail(err) ?? (err as Error).message);
    } finally {
      setProcessingAll(false);
    }
  }

  return (
    <div className="details-content">
      <div className="details-title-row">
        {renaming ? (
          <>
            <input
              className="rename-input"
              value={nameInput}
              onChange={(e) => setNameInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") void onSaveRename();
                if (e.key === "Escape") onCancelRename();
              }}
              autoFocus
              disabled={savingName}
            />
            <div className="rename-actions">
              <button type="button" className="btn-secondary" onClick={() => void onSaveRename()} disabled={savingName}>
                {t("common.save")}
              </button>
              <button type="button" className="btn-ghost" onClick={onCancelRename} disabled={savingName}>
                {t("common.cancel")}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="details-title">{project.name}</div>
            <button type="button" className="rename-btn" data-tour="rename-btn" onClick={onStartRename}>
              {t("common.rename_btn")}
            </button>
          </>
        )}
      </div>
      <div className="details-subtitle">{subtitle}</div>

      <MetaRow>
        <button
          type="button"
          className="meta-chip meta-chip--button"
          onClick={cycleLanguage}
          disabled={savingMeta}
          title={t("project_panel.cycle_hint")}
        >
          <strong>{t(LANGUAGE_KEY[project.language] ?? project.language)}</strong> {t("project_panel.language_label")}
        </button>
        <button
          type="button"
          className="meta-chip meta-chip--button"
          onClick={cycleScoreFormat}
          disabled={savingMeta}
          title={t("project_panel.cycle_hint")}
        >
          <strong>{SCORE_FORMAT_LABEL[project.score_format]}</strong> {t("project_panel.score_format_label")}
        </button>
        <button type="button" className="meta-chip meta-chip--button" onClick={() => setWeightsOpen(true)}>
          <strong>{t("weights.title")}</strong> {weightsSplit}
        </button>
      </MetaRow>

      <DetailsCard label="Progress">
        <div className="progress-bar-track">
          <div className="progress-bar-fill" style={{ width: `${pct}%` }} />
        </div>
        <div className="progress-label">
          <span>{t("project_panel.progress_label", { completed, total })}</span>
          <span>{pct}%</span>
        </div>
      </DetailsCard>

      <GradeDistributionCard projectId={project.id} />

      <div className="btn-row" style={{ marginTop: 18 }}>
        <button type="button" className="btn-secondary" onClick={() => setUploadOpen(true)}>
          {t("project_panel.add_scans_btn")}
        </button>
        <button type="button" className="btn-secondary" onClick={() => setEditOpen(true)}>
          {t("project_panel.edit_settings_btn")}
        </button>
        {project.archived ? (
          <>
            <button type="button" className="btn-ghost" disabled={archiving} onClick={() => void onUnarchiveProject()}>
              {archiving ? t("project_panel.restoring") : t("project_panel.unarchive_btn")}
            </button>
            <button type="button" className="btn-danger" disabled={deleting} onClick={() => setDeleteOpen(true)}>
              {t("project_panel.delete_btn")}
            </button>
          </>
        ) : (
          <button type="button" className="btn-ghost" disabled={archiving} onClick={() => void onArchiveProject()}>
            {archiving ? t("project_panel.archiving") : t("project_panel.archive_btn")}
          </button>
        )}
        <button
          type="button"
          className="btn-accent btn-process"
          disabled={runnable.length === 0 || processingAll}
          onClick={() => void onRunProject()}
        >
          {processingAll
            ? t("project_panel.processing")
            : t("project_panel.process_btn") + (runnable.length > 0 ? ` (${runnable.length})` : "")}
        </button>
      </div>

      <div className="details-divider" />

      <div className="export-row">
        <div className="btn-row">
          <button
            type="button"
            className="btn-ghost"
            disabled={!anyExported || exporting !== null}
            onClick={onExportProject}
          >
            {exporting === "zip" ? t("project_panel.building") : t("project_panel.export_results_btn")}
          </button>
          <button
            type="button"
            className="btn-ghost"
            disabled={!hasPapers || exporting !== null}
            onClick={onExportArchive}
          >
            {exporting === "archive" ? t("project_panel.building") : t("project_panel.export_archive_btn")}
          </button>
        </div>
        {exportNotice && <p className="export-notice">{exportNotice}</p>}
        {exportError && (
          <p className="export-error" role="alert">
            {exportError}
          </p>
        )}
      </div>

      {deleteOpen && (
        <div
          className="modal-backdrop"
          onMouseDown={(e) => { if (e.target === e.currentTarget && !deleting) setDeleteOpen(false); }}
        >
          <div className="modal-card" role="dialog" aria-modal="true">
            <button type="button" className="modal-close" onClick={() => setDeleteOpen(false)} disabled={deleting} aria-label="Close">✕</button>
            <h2 className="modal-card__title">{t("project_panel.delete_confirm_title")}</h2>
            <p style={{ margin: 0, fontSize: 13, color: "var(--text-dim)", lineHeight: 1.5 }}>
              {t("project_panel.delete_confirm_body", { name: project.name })}
            </p>
            <div className="modal-actions">
              <button type="button" className="modal-btn modal-btn--ghost" onClick={() => setDeleteOpen(false)} disabled={deleting}>
                {t("common.cancel")}
              </button>
              <button type="button" className="modal-btn modal-btn--danger" disabled={deleting} onClick={() => void onDeleteProject()}>
                {deleting ? t("project_panel.deleting") : t("project_panel.delete_confirm_btn")}
              </button>
            </div>
          </div>
        </div>
      )}
      {uploadOpen && (
        <UploadPapersDialog
          projectId={project.id}
          onClose={() => setUploadOpen(false)}
          onComplete={() => void mutate(PROJECTS_KEY)}
        />
      )}
      {editOpen && (
        <EditProjectModal
          project={project}
          onClose={() => setEditOpen(false)}
          onSaved={() => {
            setEditOpen(false);
            void mutate(PROJECTS_KEY);
            // Score format may have changed — refetch so the distribution relabels.
            void mutate(`/api/projects/${project.id}/grade_distribution`);
          }}
        />
      )}
      {weightsOpen && <WeightsPanel project={project} onClose={() => setWeightsOpen(false)} />}
    </div>
  );
}

// The six Notenpunkte → Notenstufe groups (German convention). The layout is
// always these six cells (2 rows × 3); only the labels and the numbers inside
// each cell change with the score format, so the field's shape stays constant.
const NP_GROUPS: { nps: number[]; rangeLabel: string }[] = [
  { nps: [15, 14, 13], rangeLabel: "15–13" },
  { nps: [12, 11, 10], rangeLabel: "12–10" },
  { nps: [9, 8, 7], rangeLabel: "9–7" },
  { nps: [6, 5, 4], rangeLabel: "6–4" },
  { nps: [3, 2, 1], rangeLabel: "3–1" },
  { nps: [0], rangeLabel: "0" },
];

function GradeDistributionCard({ projectId }: { projectId: number }) {
  const { t } = useTranslation();
  const { data } = useGradeDistribution(projectId);
  if (!data || !data.distribution || data.distribution.length === 0) return null;

  // 0–15: backend returns one bucket per Notenpunkt — fan each group out to its
  // three per-NP counts. Every other format returns one bucket per Notenstufe,
  // already in group order — one count per cell.
  let cells: { label: string; counts: number[] }[];
  if (data.score_format === "0-15") {
    const byNp = new Map(data.distribution.map((b) => [b.label, b.count]));
    cells = NP_GROUPS.map((g) => ({
      label: g.rangeLabel,
      counts: g.nps.map((np) => byNp.get(String(np)) ?? 0),
    }));
  } else {
    cells = NP_GROUPS.map((_g, i) => ({
      label: data.distribution[i]?.label ?? "",
      counts: [data.distribution[i]?.count ?? 0],
    }));
  }

  return (
    <DetailsCard label={t("project_panel.grade_distribution_label")}>
      <div className="grade-dist">
        {cells.map((cell) => (
          <div key={cell.label} className="grade-dist__cell">
            <span className="grade-dist__grade">{cell.label}</span>
            <div className="grade-dist__counts">
              {cell.counts.map((c, i) => (
                <span
                  key={i}
                  className={"grade-dist__count" + (c === 0 ? " grade-dist__count--zero" : "")}
                >
                  {c}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </DetailsCard>
  );
}

function triggerDownload(href: string, download?: string) {
  const link = document.createElement("a");
  link.href = href;
  if (download) link.download = download;
  document.body.appendChild(link);
  link.click();
  link.remove();
}

function extractDetail(err: unknown): string | null {
  if (err instanceof ApiError && err.body && typeof err.body === "object") {
    const d = (err.body as { detail?: unknown }).detail;
    if (typeof d === "string") return d;
  }
  return null;
}
